import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useStyleConfig} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import ProductBox from './partials/product-box'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {GalleryChevronLeftIcon, GalleryChevronRightIcon} from '../../icons'

export const ProductMoodboard = ({productMoodboardGroup = []}) => {
    const uniqueId = useId().replace(/:/g, '')
    const sliderId = `product-moodboard-${uniqueId}`
    const prevButtonClass = `swiper-button-prev-${uniqueId}`
    const nextButtonClass = `swiper-button-next-${uniqueId}`
    const paginationClass = `product-moodboard-pagination-${uniqueId}`

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('ProductMoodboard')
    const {currency} = useCurrency()

    const syncPaginationTabState = () => {
        if (typeof document === 'undefined') {
            return
        }

        const paginationRoot = document.querySelector(`#${sliderId} .${paginationClass}`)
        if (!paginationRoot) {
            return
        }

        const tabButtons = paginationRoot.querySelectorAll('button[role="tab"]')
        tabButtons.forEach((button) => {
            const isActive = button.classList.contains('swiper-pagination-bullet-active')
            button.setAttribute('aria-selected', isActive ? 'true' : 'false')
            button.setAttribute('tabindex', isActive ? '0' : '-1')
        })
    }

    var productIds = []
    productMoodboardGroup.forEach((group) =>
        group.products?.data.forEach((p) => productIds.push(p.id)),
    )

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'images'],
                currency,
            },
            headers: {
                'x-debug': 'prod-moodboard-index',
            },
        },
        {
            enabled: productIds?.length > 0,
        },
    )

    return (
        productMoodboardGroup?.length > 0 && (
            <Box
                data-testid="product-moodboard"
                data-cs-label="editorial-product_moodboard"
                {...styles.container}
            >
                <Box __css={{...swiperStyles, ...styles.content}} id={sliderId}>
                    <Flex
                        className={`product-moodboard-pagination ${paginationClass}`}
                        role="tablist"
                        aria-label="Product moodboard buckets"
                        {...styles.pagination}
                    />
                    <HideOnMobile>
                        <Flex
                            {...styles.heroLeftRightArrowsContainer}
                            left={remCalc(16)}
                            top={'50%'}
                        >
                            <Flex
                                {...styles.heroLeftRightArrows}
                                className={`swiper-button-prev ${prevButtonClass}`}
                                data-testid="swiper-button-prev"
                                tabIndex={0}
                                role="button"
                                aria-label="swiper button prev icon"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        e.currentTarget.click()
                                    }
                                }}
                            >
                                <GalleryChevronLeftIcon boxSize={remCalc(16)} />
                            </Flex>
                        </Flex>
                        <Flex
                            {...styles.heroLeftRightArrowsContainer}
                            right={remCalc(16)}
                            top={'50%'}
                        >
                            <Flex
                                {...styles.heroLeftRightArrows}
                                className={`swiper-button-next ${nextButtonClass}`}
                                data-testid="swiper-button-next"
                                tabIndex={0}
                                role="button"
                                aria-label="swiper button next icon"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        e.currentTarget.click()
                                    }
                                }}
                            >
                                <GalleryChevronRightIcon boxSize={remCalc(16)} />
                            </Flex>
                        </Flex>
                    </HideOnMobile>
                    <Swiper
                        key={'product-moodboard-swiper'}
                        modules={[Navigation, Pagination, Keyboard]}
                        onInit={syncPaginationTabState}
                        onSlideChange={syncPaginationTabState}
                        navigation={{
                            prevEl: `.${prevButtonClass}`,
                            nextEl: `.${nextButtonClass}`,
                        }}
                        pagination={{
                            clickable: true,
                            el: `#${sliderId} .${paginationClass}`,
                            renderBullet: (index, className) => {
                                const id = `product-moodboard-tab-${index}`
                                const panelId = `product-moodboard-panel-${index}`
                                const isActive = className.includes(
                                    'swiper-pagination-bullet-active',
                                )
                                return `<button role="tab" id="${id}" aria-controls="${panelId}" aria-selected="${isActive}" tabindex="${
                                    isActive ? '0' : '-1'
                                }" class="${className} product-moodboard-bucket-tab">${
                                    productMoodboardGroup[index].label
                                }</button>`
                            },
                        }}
                        className={`product-moodboard-swiper`}
                    >
                        {productMoodboardGroup.map((tile, tIndex) => {
                            const hasTwoTiles = tile.products?.data?.length == 2
                            const hasThreeTiles = tile.products?.data?.length == 3
                            const hasFourTiles = tile.products?.data?.length == 4
                            const hasFiveTiles = tile.products?.data?.length == 5
                            return (
                                <SwiperSlide
                                    key={`product-moodboard-tile-${tIndex}`}
                                    role="tabpanel"
                                    id={`product-moodboard-panel-${tIndex}`}
                                    aria-labelledby={`product-moodboard-tab-${tIndex}`}
                                >
                                    <Flex {...styles.boardcontainer}>
                                        <Flex {...styles.boardfirsthalf}>
                                            <ProductBox
                                                product={{
                                                    ...productsData?.data?.find(
                                                        (product) =>
                                                            product.id == tile.products?.data[0].id,
                                                    ),
                                                }}
                                                styleVariant={'default'}
                                            />
                                        </Flex>
                                        <Flex {...styles.boardsecondhalf}>
                                            {hasTwoTiles && (
                                                <ProductBox
                                                    product={{
                                                        ...productsData?.data?.find(
                                                            (product) =>
                                                                product.id ==
                                                                tile.products?.data[1].id,
                                                        ),
                                                    }}
                                                    styleVariant={'default'}
                                                />
                                            )}
                                            {hasThreeTiles && (
                                                <>
                                                    <ProductBox
                                                        product={{
                                                            ...productsData?.data?.find(
                                                                (product) =>
                                                                    product.id ==
                                                                    tile.products?.data[1].id,
                                                            ),
                                                        }}
                                                        styleVariant={'mediumTile'}
                                                    />
                                                    <ProductBox
                                                        product={{
                                                            ...productsData?.data?.find(
                                                                (product) =>
                                                                    product.id ==
                                                                    tile.products?.data[2].id,
                                                            ),
                                                        }}
                                                        styleVariant={'mediumTile'}
                                                    />
                                                </>
                                            )}
                                            {hasFourTiles && (
                                                <>
                                                    <ProductBox
                                                        product={{
                                                            ...productsData?.data?.find(
                                                                (product) =>
                                                                    product.id ==
                                                                    tile.products?.data[1].id,
                                                            ),
                                                        }}
                                                        styleVariant={'mediumTile'}
                                                    />
                                                    <Flex {...styles.boardtwoboxcontainer}>
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[2].id,
                                                                ),
                                                            }}
                                                            styleVariant={'smallTile'}
                                                        />
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[3].id,
                                                                ),
                                                            }}
                                                            styleVariant={'smallTile'}
                                                        />
                                                    </Flex>
                                                </>
                                            )}
                                            {hasFiveTiles && (
                                                <>
                                                    <Flex {...styles.boardtwoboxcontainer}>
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[1].id,
                                                                ),
                                                            }}
                                                            styleVariant={'smallTile'}
                                                        />
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[2].id,
                                                                ),
                                                            }}
                                                            styleVariant={'smallTile'}
                                                        />
                                                    </Flex>
                                                    <Flex {...styles.boardtwoboxcontainer}>
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[3].id,
                                                                ),
                                                            }}
                                                            styleVariant={'smallTile'}
                                                        />
                                                        <ProductBox
                                                            product={{
                                                                ...productsData?.data?.find(
                                                                    (product) =>
                                                                        product.id ==
                                                                        tile.products?.data[4].id,
                                                                ),
                                                            }}
                                                            styleVariant={'smallTile'}
                                                        />
                                                    </Flex>
                                                </>
                                            )}
                                        </Flex>
                                    </Flex>
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>
                </Box>
            </Box>
        )
    )
}

ProductMoodboard.propTypes = {
    productMoodboardGroup: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            products: PropTypes.shape({
                data: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string,
                        name: PropTypes.string,
                        image: PropTypes.shape({
                            alt: PropTypes.string,
                            link: PropTypes.string,
                            title: PropTypes.string,
                        }),
                        currency: PropTypes.string,
                        price: PropTypes.number,
                    }),
                ),
            }),
        }),
    ),
}
